import { brandLinka } from '@ads/tokens';
import { DashboardDailySales } from '@linka/domain/dtos';
import { useTranslation } from '@linka/i18n';
import { formatDate, formatMoney, formatNumber } from '@linka/utils';
import { CartesianGrid, LabelList, Line, LineChart, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../chart/chart';
import { ChartCard, ChartCardContent, ChartCardHeader, ChartCardTitle, ExportChartTriggerButton } from '../components';

interface SalesTotalByDayChartProps {
  data: DashboardDailySales;
}
export const SalesTotalByDayChart = ({ data }: SalesTotalByDayChartProps) => {
  const t = useTranslation();

  const dailySalesConfig = {
    date: { label: t('date') },
    salesCount: { label: t('sales'), color: brandLinka.contextualColor.interactive.primary },
    totalAmount: { label: t('total-amount'), color: brandLinka.baseColor.brand['500'] },
  };
  return (
    <ChartCard>
      <ChartCardHeader>
        <ChartCardTitle>{t('dashboard.charts.total-revenue-by-day')}</ChartCardTitle>
        <ExportChartTriggerButton type='OVERVIEW_SALES_TOTAL_BY_DAY' />
      </ChartCardHeader>
      {data.data.length === 0 ? (
        <ChartCardContent className='flex flex-1 items-center justify-center py-xl'>
          <span className='ts-paragraph-xs text-ctx-content-base'>{t('dashboard.charts.no-data')}</span>
        </ChartCardContent>
      ) : (
        <ChartCardContent className='flex'>
          <ChartContainer config={dailySalesConfig} className='w-[60px]'>
            <LineChart accessibilityLayer data={data.data} margin={{ top: 12, right: 12, left: 5, bottom: 10 }}>
              <YAxis
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => formatNumber(value, { notation: 'compact' })}
                width={60}
              />
              <Line dataKey='totalAmount' stroke='transparent' fill='transparent' />
            </LineChart>
          </ChartContainer>

          <div className='flex-1 overflow-x-auto'>
            <ChartContainer config={dailySalesConfig} className='w-full' dataLength={data.data.length}>
              <LineChart accessibilityLayer data={data.data} margin={{ top: 16, right: 12, left: 5, bottom: 10 }}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey='date' tick={false} axisLine={false} padding={{ left: 30, right: 30 }} />
                <ChartTooltip
                  labelFormatter={(label) => formatDate({ date: label, dateFormat: 'dd/MM/yyyy' }) ?? label}
                  formatter={(value) => formatMoney(Number(value))}
                  cursor={{ fill: brandLinka.contextualColor.interactive.secondary }}
                  content={<ChartTooltipContent />}
                />
                <Line
                  dataKey='totalAmount'
                  stroke='var(--color-totalAmount)'
                  fill='var(--color-totalAmount)'
                  dot={{
                    r: 2,
                    fill: 'var(--color-totalAmount)',
                  }}
                  activeDot={{
                    r: 5,
                  }}
                >
                  <LabelList
                    dataKey='totalAmount'
                    position='top'
                    fontSize={14}
                    formatter={(label) => formatNumber(Number(label))}
                  />
                </Line>
              </LineChart>
            </ChartContainer>
          </div>
        </ChartCardContent>
      )}
    </ChartCard>
  );
};
